import React, { useState } from 'react';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { Upload, CheckCircle, AlertCircle, X, Loader2, QrCode, Smartphone } from 'lucide-react';

const WaveQRUpload = ({ storage, db, userProfile, onUploadComplete }) => {
  const [qrFile, setQrFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Nettoyer les messages
  const clearMessages = () => {
    setError('');
    setSuccess('');
  };

  // Gestion du changement de fichier
  const handleFileChange = (e) => {
    clearMessages();
    const file = e.target.files[0];
    
    if (!file) {
      setQrFile(null);
      return;
    }

    // Vérifier que c'est une image
    if (!file.type.startsWith('image/')) {
      setError('Veuillez sélectionner une image (PNG, JPG, JPEG)');
      return;
    }

    // Vérifier la taille (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setError('L\'image est trop grande (max 2MB)');
      return;
    }

    setQrFile(file);
  };

  // Upload du fichier
  const handleUpload = async () => {
    if (!qrFile) {
      setError('Veuillez sélectionner un QR code');
      return;
    }

    // Vérification de la disponibilité du storage
    if (!storage) {
      setError('Erreur de configuration: Firebase Storage n\'est pas disponible.');
      return;
    }

    setUploading(true);
    clearMessages();

    try {
      // 1. Upload vers Firebase Storage
      const cleanFileName = qrFile.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const storagePath = `system/wave_qr_code/${Date.now()}_${cleanFileName}`;
      const storageRef = ref(storage, storagePath);
      
      const uploadTask = uploadBytesResumable(storageRef, qrFile);
      await uploadTask;
      const snapshot = uploadTask.snapshot;

      // 2. Récupérer l'URL publique
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      // 3. Sauvegarder l'URL dans Firestore
      const configRef = doc(db, 'system_config', 'wave_qr_code');
      await setDoc(configRef, {
        qrCodeUrl: downloadURL,
        updatedAt: serverTimestamp(),
        updatedBy: userProfile?.uid,
        updatedByEmail: userProfile?.email,
        merchantPhone: process.env.REACT_APP_WAVE_MERCHANT_PHONE || '773620212',
        fileName: cleanFileName,
        fileSize: qrFile.size,
        fileType: qrFile.type,
        isPureQRCode: true,
        lastUpdated: new Date().toISOString()
      }, { merge: true });

      setSuccess('QR code Wave configuré avec succès !');
      
      // 4. Appeler le callback pour informer le parent
      if (onUploadComplete) {
        onUploadComplete(downloadURL);
      }

      // 5. Réinitialiser le formulaire
      setQrFile(null);

    } catch (err) {
      console.error('Erreur upload QR:', err);
      setError(`Erreur lors de l'upload: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  const removeFile = () => {
    setQrFile(null);
    clearMessages();
  };

  // Vérifier si l'utilisateur est Super Admin
  const isSuperAdmin = userProfile?.role === 'superAdmin';

  if (!isSuperAdmin) {
    return (
      <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-6">
        <div className="text-center p-8">
          <AlertCircle className="mx-auto text-red-500 mb-4" size={48} />
          <h2 className="text-xl font-bold text-red-800 mb-2">Accès Refusé</h2>
          <p className="text-gray-600">
            Seul le Super Administrateur peut configurer le QR code Wave.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-6">
      <div className="flex items-center mb-6">
        <QrCode className="text-purple-600 mr-3" size={28} />
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Configuration QR Code Wave</h2>
          <p className="text-gray-600 text-sm">Super Admin seulement</p>
        </div>
      </div>

      <div className="mb-6">
        <p className="text-gray-600 mb-4">
          Téléchargez le QR code Wave marchand. Ce QR code sera utilisé par toutes les écoles pour payer leur abonnement.
        </p>
        
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
          <h3 className="font-semibold text-purple-800 mb-2">📋 Instructions IMPORTANTES :</h3>
          <ul className="text-sm text-purple-700 space-y-2">
            <li>✅ <strong>Le QR code doit être PUR</strong> - pas de logo superposé au centre</li>
            <li>✅ Téléchargez directement depuis votre compte Wave marchand</li>
            <li>✅ Format recommandé : PNG avec fond blanc</li>
            <li>✅ Taille minimale : 300x300 pixels</li>
            <li>❌ PAS de logo, texte ou image au centre du QR code</li>
            <li>❌ PAS de modification de l'image après téléchargement</li>
          </ul>
        </div>
      </div>

      {/* Zone de téléversement SIMPLIFIÉE - SANS prévisualisation */}
      <div 
        className={`border-2 border-dashed rounded-lg p-8 text-center mb-4 transition-colors ${
          qrFile ? 'border-green-400 bg-green-50' : 'border-gray-300 hover:border-purple-400'
        }`}
      >
        {qrFile ? (
          <div className="text-center">
            <div className="mb-4">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-3">
                <QrCode className="text-green-600" size={32} />
              </div>
              <p className="font-semibold text-gray-800 mb-1">{qrFile.name}</p>
              <p className="text-sm text-gray-600">
                {(qrFile.size / 1024).toFixed(0)} KB • {qrFile.type.replace('image/', '').toUpperCase()}
              </p>
            </div>
            
            <div className="flex justify-center space-x-3">
              <button
                onClick={removeFile}
                className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 flex items-center transition duration-200"
              >
                <X size={16} className="mr-2" />
                Changer de fichier
              </button>
            </div>
            
            <p className="text-green-600 font-medium mt-3 flex items-center justify-center">
              <CheckCircle className="mr-2" size={18} />
              Fichier sélectionné ✓
            </p>
          </div>
        ) : (
          <>
            <Upload className="mx-auto text-gray-400 mb-4" size={48} />
            <p className="text-gray-600 mb-2">
              <span className="font-semibold">Glissez-déposez</span> votre QR code ici
            </p>
            <p className="text-gray-500 text-sm mb-4">ou</p>
            <label className="cursor-pointer bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 inline-block transition duration-200">
              <Upload className="inline mr-2" size={18} />
              Sélectionner un fichier
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
            </label>
            <p className="text-gray-500 text-xs mt-4">PNG, JPG, JPEG (max 2MB)</p>
          </>
        )}
      </div>

      {/* Informations de configuration */}
      <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <h4 className="font-semibold text-blue-800 mb-2 flex items-center">
          <Smartphone className="mr-2" size={18} />
          Configuration du paiement :
        </h4>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-blue-700">Numéro Wave marchand :</span>
            <span className="font-semibold">{process.env.REACT_APP_WAVE_MERCHANT_PHONE || '773620212'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-blue-700">Montant fixe :</span>
            <span className="font-semibold">10,000 FCFA</span>
          </div>
          <div className="flex justify-between">
            <span className="text-blue-700">Validité :</span>
            <span className="font-semibold">30 jours</span>
          </div>
          <div className="flex justify-between">
            <span className="text-blue-700">Pour :</span>
            <span className="font-semibold">Toutes les écoles</span>
          </div>
        </div>
      </div>

      {/* Messages d'état */}
      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-lg flex items-center">
          <AlertCircle className="mr-2 flex-shrink-0" size={20} />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="mb-4 p-3 bg-green-100 border border-green-300 text-green-700 rounded-lg flex items-center">
          <CheckCircle className="mr-2 flex-shrink-0" size={20} />
          <span>{success}</span>
        </div>
      )}

      {/* Bouton d'upload */}
      <button
        onClick={handleUpload}
        disabled={!qrFile || uploading}
        className="w-full bg-purple-600 text-white py-3 rounded-lg font-semibold hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200 flex items-center justify-center"
      >
        {uploading ? (
          <>
            <Loader2 className="animate-spin mr-2" size={20} />
            Upload en cours...
          </>
        ) : (
          <>
            <Upload className="mr-2" size={20} />
            {qrFile ? 'Configurer le QR code Wave' : 'Sélectionnez d\'abord un fichier'}
          </>
        )}
      </button>

      {/* Conseils de scan */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <h4 className="font-semibold text-gray-700 mb-2 flex items-center">
          <QrCode className="mr-2" size={18} />
          Conseils pour un bon QR code :
        </h4>
        <ul className="text-sm text-gray-600 space-y-1">
          <li>• <strong>Testez le scan</strong> avant de l'uploader</li>
          <li>• Utilisez un QR code <strong>noir sur fond blanc</strong></li>
          <li>• Évitez les dégradés ou motifs en arrière-plan</li>
          <li>• La taille minimum recommandée est 300x300px</li>
          <li>• Le QR code doit pointer vers votre numéro Wave</li>
        </ul>
      </div>

      {/* Information sur l'utilisateur */}
      <div className="mt-4 text-xs text-gray-500 text-center">
        <p>
          Connecté en tant que : <span className="font-semibold">{userProfile?.email}</span>
        </p>
        <p>
          Rôle : <span className="font-semibold text-purple-600">Super Admin</span>
        </p>
      </div>
    </div>
  );
};

export default WaveQRUpload;